import { useState, useEffect, useCallback, useRef } from 'react';
import { getStudentStreak } from '../api/streaks';
import { trackStreakUpdated, trackStreakBroken } from '../../../utils/posthogEvents';
import { reportUserFacingError } from '../../../utils/errorReporting';
import { useAuth } from '../../../app/providers/AuthContext';

/**
 * @typedef {Object} StreakData
 * @property {number} current_streak - Consecutive days with a submission
 * @property {number} points - Derived 积分 total. Computed from state by
 *   backend/features/gamification/points, not summed on the client.
 * @property {Object} [breakdown] - Per-rule counts behind `points`
 */

/**
 * Fetches and manages student streak/coin data.
 *
 * Implicit dependency: this hook calls `useAuth()` internally to read
 * `consumePrefetchedStreaks` (Phase 5 bootstrap handoff). Must be rendered
 * inside an `<AuthProvider>`. Tests mock `../../../app/providers/AuthContext`.
 *
 * @param {string} studentId
 * @param {boolean} isSignedIn
 * @param {Object} user
 * @returns {{
 *   streakData: StreakData | null,
 *   loading: boolean,
 *   refetch: () => Promise<void>
 * }}
 */
export function useStreakData(studentId, isSignedIn, user) {
  // Phase 5: /api/bootstrap may have supplied streaks for this studentId.
  // One-shot consumption; AuthContext nulls the ref after the first read so
  // a later student switch always triggers a fresh fetch. If the prefetch
  // studentId doesn't match (family switch from the cached boot student),
  // consume returns null and we fall through to the normal fetch path.
  const { consumePrefetchedStreaks } = useAuth();
  const [initialPrefetch] = useState(() =>
    consumePrefetchedStreaks?.(studentId) ?? null
  );
  const [streakData, setStreakData] = useState(initialPrefetch?.streaks || null);
  const [loading, setLoading] = useState(!initialPrefetch);

  // Track previous streak for change detection. The tuple ensures the baseline
  // is scoped to a specific student — switching students atomically resets the
  // baseline via the fetch effect below.
  const prevStreakRef = useRef({ studentId: null, streak: null });

  useEffect(() => {
    const current = streakData?.current_streak;
    if (current === undefined || current === null) return;
    const prev = prevStreakRef.current;
    // Skip if streakData still belongs to the previous student — the fetch
    // effect hasn't reset it yet and we'd be comparing across students.
    if (prev.studentId !== null && prev.studentId !== studentId) return;
    // Skip the first load (prev.streak === null means we have no baseline)
    if (prev.streak !== null && prev.streak !== current) {
      if (current === 0 && prev.streak > 0) {
        trackStreakBroken({ studentId, previousStreakDays: prev.streak });
      } else if (current > prev.streak) {
        trackStreakUpdated({ studentId, streakDays: current });
      }
    }
    prevStreakRef.current = { studentId, streak: current };
  }, [streakData, studentId]);

  const refetch = useCallback(async () => {
    if (!studentId || !isSignedIn || !user) return;

    try {
      const data = await getStudentStreak(studentId);
      setStreakData(data);
    } catch (error) {
      reportUserFacingError(error, { context: 'Load student streak (refetch)' });
    }
  }, [studentId, isSignedIn, user]);

  // Track whether the initial prefetch already hydrated state for this mount.
  // Prevents the main effect below from resetting to null + refetching when
  // we already have fresh data from bootstrap.
  const hydratedFromPrefetchRef = useRef(!!initialPrefetch);

  useEffect(() => {
    // If bootstrap seeded this mount, skip the reset/refetch on first run.
    if (hydratedFromPrefetchRef.current) {
      hydratedFromPrefetchRef.current = false;
      prevStreakRef.current = { studentId, streak: null };
      return undefined;
    }
    // Reset data + baseline atomically when studentId changes so stale data
    // from the previous student can't trigger false streak events, and the UI
    // shows a loading state instead of stale numbers during the fetch.
    setStreakData(null);
    setLoading(true);
    prevStreakRef.current = { studentId, streak: null };

    let cancelled = false;

    const loadStreakData = async () => {
      if (!studentId || !isSignedIn || !user) {
        setLoading(false);
        return;
      }

      try {
        const data = await getStudentStreak(studentId);
        if (cancelled) return;
        setStreakData(data);
      } catch (error) {
        if (cancelled) return;
        reportUserFacingError(error, { context: 'Load student streak (initial)' });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStreakData();

    return () => {
      cancelled = true;
    };
  }, [studentId, isSignedIn, user]);

  return { streakData, setStreakData, loading, refetch };
}

export default useStreakData;
